import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { MdEdit, MdOpenInNew, MdRefresh } from 'react-icons/md';
import type { AppCatalogEntry } from '@api/apps';
import { FeatureGate, useFeatureGate } from '@featureFlags/index';
import { usesAgent } from '../appCapabilities';
import { canEdit } from '../appPermissions';
import type { AppRunController } from '../appFrame/useAppRunController';
import type { AppAction } from '../builder/AppActionsMenu';
import { builderPath } from '../builder/userAppStatus';

export const useRunMenuActions = (controller: AppRunController, catalogEntry: AppCatalogEntry | null): AppAction[] => {
  const intl = useIntl();
  const navigate = useNavigate();
  const builderEnabled = useFeatureGate(FeatureGate.OS_APP_BUILDER);
  const { run, manifest } = controller;
  const agent = usesAgent(manifest);
  const started = Boolean(run) && (controller.isBusy || run?.status !== 'DRAFT');
  const appKey = catalogEntry?.appKey;

  const actions: (AppAction | false)[] = [
    agent &&
      started && {
        id: 'transcript',
        label: intl.formatMessage({ id: 'apps.run.transcript', defaultMessage: 'View transcript' }),
        icon: <MdOpenInNew size={16} />,
        onSelect: controller.openTranscript,
      },
    agent && {
      id: 'new-run',
      label: intl.formatMessage({ id: 'apps.run.startOver', defaultMessage: 'Start over' }),
      icon: <MdRefresh size={16} />,
      onSelect: controller.newRun,
    },
    builderEnabled &&
      appKey !== undefined &&
      canEdit(catalogEntry ?? {}) && {
        id: 'edit',
        label: intl.formatMessage({ id: 'apps.run.menu.edit', defaultMessage: 'Edit' }),
        icon: <MdEdit size={16} />,
        onSelect: () => navigate(builderPath(appKey)),
      },
  ];
  return actions.filter((action): action is AppAction => action !== false);
};
